import axios from 'axios';
import apiClient from './apiClient';

// OpenAPI-compliant interfaces for API Endpoint Management
export interface ApiEndpoint {
  id: number;
  uuid: string;
  platform: number;
  platform_name: string;
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  summary: string;
  description: string;
  parameters: object;
  request_body: object;
  responses: object;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface ApiEndpointCreate {
  platform: number;
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  summary: string;
  description?: string;
  parameters?: object;
  tags?: string[];
}

export interface ApiEndpointUpdate {
  summary?: string;
  description?: string;
  parameters?: object;
  tags?: string[];
}

export interface ApiEndpointListResponse {
  count: number;
  results: ApiEndpoint[];
}

export interface ApiEndpointListParams {
  platform?: number;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  search?: string;
}

class ApiEndpointService {
  async getApiEndpoints(params?: ApiEndpointListParams): Promise<ApiEndpoint[]> {
    try {
      const response = await apiClient.get<ApiEndpointListResponse>('/api/api-endpoints/', { params });
      return response.data.results;
    } catch (error) {
      console.error('Failed to fetch API endpoints:', error);
      throw this.handleError(error);
    }
  }

  async getApiEndpoint(id: number): Promise<ApiEndpoint> {
    try {
      const response = await apiClient.get<ApiEndpoint>(`/api/api-endpoints/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch API endpoint ${id}:`, error);
      throw this.handleError(error);
    }
  }

  async createApiEndpoint(data: ApiEndpointCreate): Promise<ApiEndpoint> {
    try {
      const response = await apiClient.post<ApiEndpoint>('/api/api-endpoints/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create API endpoint:', error);
      throw this.handleError(error);
    }
  }

  async updateApiEndpoint(id: number, data: ApiEndpointUpdate): Promise<ApiEndpoint> {
    try {
      const response = await apiClient.put<ApiEndpoint>(`/api/api-endpoints/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update API endpoint ${id}:`, error);
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      if (errorData && typeof errorData === 'object' && 'error' in errorData) {
        return new Error(errorData.error);
      }
      return new Error(`HTTP ${error.response.status}: ${error.response.statusText}`);
    }
    return error instanceof Error ? error : new Error('Unknown error occurred');
  }
}

export const apiEndpointService = new ApiEndpointService();